import {
  aiSystemAssessmentRouteUtil,
  aiSystemBundleRouteUtil,
  aiSystemAssessmentBundleVersionsRouteUtil,
  functionalModelAssessmentRouteUtil,
  functionalModelBundleRouteUtil,
  functionalModelAssessmentBundleVersionsRouteUtil,
  candidateModelAssessmentRouteUtil,
  candidateModelBundleRouteUtil,
  candidateModelAssessmentBundleVersionsRouteUtil,
  aiSystemPolicyRouteUtil,
} from '~/client/routes';
import * as GQL from '~/graphql/generated/client';
import { JurisdictionMapping } from '~/shared/configurations/jurisdiction-mapping';
import { Color, DesignToken } from '~c/components/base/core';
interface RouteHelpers {
  assessment:
    | ReturnType<typeof aiSystemAssessmentRouteUtil.useHelper>
    | ReturnType<typeof functionalModelAssessmentRouteUtil.useHelper>
    | ReturnType<typeof candidateModelAssessmentRouteUtil.useHelper>;
  bundle?:
    | ReturnType<typeof aiSystemBundleRouteUtil.useHelper>
    | ReturnType<typeof functionalModelBundleRouteUtil.useHelper>
    | ReturnType<typeof candidateModelBundleRouteUtil.useHelper>;
  version?:
    | ReturnType<typeof aiSystemAssessmentBundleVersionsRouteUtil.useHelper>
    | ReturnType<
        typeof functionalModelAssessmentBundleVersionsRouteUtil.useHelper
      >
    | ReturnType<
        typeof candidateModelAssessmentBundleVersionsRouteUtil.useHelper
      >;
}

interface NavigateToAssessmentParams {
  assessmentId: string;
  project: {
    projectId?: string;
    functionalModelId?: string;
    candidateId?: string;
    agentId?: string;
  };
  routeUtils: RouteHelpers;
}

export const navigateToAssessment = ({
  assessmentId,
  project,
  routeUtils,
}: NavigateToAssessmentParams) => {
  const { projectId, functionalModelId, candidateId, agentId } = project;

  try {
    // Navigate based on the context
    if (candidateId && functionalModelId && projectId) {
      return routeUtils.assessment.navigateTo('LANDING', {
        params: {
          projectId,
          functionalModelId,
          candidateId: candidateId ?? agentId ?? '',
          assessmentId,
        },
      });
    }

    if (functionalModelId && projectId) {
      return routeUtils.assessment.navigateTo('LANDING', {
        params: {
          projectId,
          functionalModelId,
          assessmentId,
        },
      });
    }

    if (projectId) {
      return routeUtils.assessment.navigateTo('LANDING', {
        params: {
          projectId,
          assessmentId,
        },
      });
    }

    console.error('Unable to navigate: missing required IDs');
  } catch (error) {
    console.error('Navigation error:', error);
  }
};

interface NavigateToBundleParams {
  bundleId: string;
  versionId?: string;
  project: {
    projectId?: string;
    functionalModelId?: string;
    candidateId?: string;
    agentId?: string;
    vendorModelId?: string;
    vendorAgentId?: string;
  };
  routeUtils: RouteHelpers;
  state?: any;
}

export const navigateToBundle = ({
  bundleId,
  versionId,
  project,
  routeUtils,
  state,
}: NavigateToBundleParams) => {
  const {
    projectId,
    functionalModelId,
    candidateId,
    agentId,
    vendorModelId,
    vendorAgentId,
  } = project;

  try {
    const params = {
      projectId: projectId ?? '',
      functionalModelId: functionalModelId ?? '',
      candidateId: candidateId ?? agentId ?? '',
      vendorModelId: vendorModelId ?? '',
      vendorAgentId: vendorAgentId ?? '',
      agentId: agentId ?? '',
      bundleId,
      ...(versionId && { versionId }),
    };

    // If version ID is provided, navigate to version view
    if (versionId) {
      return routeUtils?.version?.navigateTo('VERSIONS_IDS', { params, state });
    }

    // Otherwise navigate to bundle form
    const result = routeUtils?.bundle?.navigateTo('BUNDLE_FORM', {
      params,
      state,
    });
    return result;
  } catch (error) {
    console.error('Navigation error:', error);
  }
};

interface NavigateToRiskOverTimeParams {
  bundleId: string;
  project: {
    projectId?: string;
    functionalModelId?: string;
    candidateId?: string;
    agentId?: string;
    vendorModelId?: string;
    vendorAgentId?: string;
  };
  routeUtils: RouteHelpers;
}

export const navigateToRiskOverTime = ({
  bundleId,
  project,
  routeUtils,
}: NavigateToRiskOverTimeParams) => {
  const {
    projectId,
    functionalModelId,
    candidateId,
    agentId,
    vendorModelId,
    vendorAgentId,
  } = project;

  try {
    return routeUtils?.bundle?.navigateTo('RISK_OVER_TIME', {
      params: {
        projectId: projectId ?? '',
        functionalModelId: functionalModelId ?? '',
        candidateId: candidateId ?? agentId ?? '',
        vendorModelId: vendorModelId ?? '',
        vendorAgentId: vendorAgentId ?? '',
        agentId: agentId ?? '',
        bundleId,
      },
    });
  } catch (error) {
    console.error('Navigation error:', error);
  }
};

export const toTitleCase = (str: string): string => {
  const minorWords = new Set([
    'a',
    'an',
    'the',
    'and',
    'but',
    'or',
    'for',
    'nor',
    'in',
    'on',
    'at',
    'to',
    'of',
    'with',
  ]);

  return str
    .toLowerCase()
    .split(' ')
    .map((word, index, arr) => {
      // Always capitalize first and last word, or if it's not a minor word
      if (index === 0 || index === arr.length - 1 || !minorWords.has(word)) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    })
    .join(' ');
};

/**
 * Converts AssessmentCategoryStatus enum to string format for ProjectTable component
 */
export const convertAssessmentCategoryStatus = (
  status: GQL.AssessmentCategoryStatus | undefined
): 'N_A' | 'INCOMPLETE' | 'PASS' | 'FAIL' | undefined => {
  switch (status) {
    case GQL.AssessmentCategoryStatus.NA:
      return 'N_A';
    case GQL.AssessmentCategoryStatus.Incomplete:
      return 'INCOMPLETE';
    case GQL.AssessmentCategoryStatus.Pass:
      return 'PASS';
    case GQL.AssessmentCategoryStatus.Fail:
      return 'FAIL';
    default:
      return undefined;
  }
};

/**
 * Calculates the number of columns for ProjectTable based on available data and user permissions
 */
export const calculateProjectTableColumns = ({
  hasSecurityData,
  hasPrivacyData,
  hasBiasData,
  hasPerformanceData,
  isAdmin,
}: {
  hasSecurityData: boolean;
  hasPrivacyData: boolean;
  hasBiasData: boolean;
  hasPerformanceData: boolean;
  isAdmin: boolean;
}): number => {
  let baseColumns = 5; // NAME, SOURCE, RESIDUAL RISK, ASSESSMENT COMPLETION, POLICY ALIGNMENT
  if (hasSecurityData) {
    baseColumns++;
  }
  if (hasPrivacyData) {
    baseColumns++;
  }
  if (hasBiasData) {
    baseColumns++;
  }
  if (hasPerformanceData) {
    baseColumns++;
  }
  if (isAdmin) {
    baseColumns++; // Action column
  }
  return baseColumns;
};

export const isPolicyRoute = (
  currentRoute: string | undefined,
  pathname: string
) => {
  const isAiSystemPolicy = currentRoute === aiSystemPolicyRouteUtil.routePrefix;
  const isFunctionalModelPolicy =
    pathname.includes('/ai-usecase/') && pathname.endsWith('/add-policy');
  return isAiSystemPolicy || isFunctionalModelPolicy;
};

export type ProjectContextType =
  | 'Use Case'
  | 'Create Project'
  | 'Functional Model'
  | 'Model'
  | 'Dataset'
  | 'Project'
  | 'Agent';

export const determineProjectContext = (
  pathname: string,
  projectId?: string,
  functionalModelId?: string,
  candidateId?: string
): ProjectContextType => {
  // Check specific creation routes first
  if (pathname.includes('/dashboard/create-ai-system')) {
    return 'Create Project';
  }

  if (pathname.includes('/create-ai-usecase')) {
    return 'Use Case';
  }

  if (pathname.includes('/create-candidate')) {
    return 'Model';
  }

  if (pathname.includes('/create-dataset')) {
    return 'Dataset';
  }
  if (pathname.includes('/create-agent')) {
    return 'Agent';
  }

  // Fall back to checking URL parameters for existing resource contexts
  if (candidateId) {
    return 'Model';
  }

  if (functionalModelId) {
    return 'Functional Model';
  }

  if (projectId) {
    return 'Use Case';
  }

  return 'Project';
};

// --- Vulnerability Details Drawer Helpers ---

// Types from vulnerability-details-drawer.tsx
export interface ProbeResponseEntry {
  entry_type: string;
  uuid?: string;
  seq?: number;
  status?: number;
  probe_classname?: string;
  probe_params?: Record<string, any>;
  prompt?: string;
  outputs?: string[];
  detector_results?: Record<string, number[]>;
  goal?: string;
  messages?: { role: string; content: string }[][];
  probe?: string;
  detector?: string;
  passed?: number;
  total?: number;
  [key: string]: any;
}

export interface ProbeResponse {
  __typename?: string;
  artifact_url?: string;
  probe: string;
  category: string;
  display_name?: string;
  display_category?: string;
  timestamp: string;
  responses: ProbeResponseEntry[];
  createdAt: number;
  mlflowRunId?: string;
}

export type VulnerabilityDataType =
  | {
      probe_classname: string;
      goal: string;
      prompt: string;
      outputs: string[];
      detector_results: Record<string, number[]>;
      status: number;
      messages: { role: string; content: string }[][];
      uuid: string;
      seq: number;
      category: string;
      probeName: string;
      artifact_url: string;
    }[]
  | ProbeResponse[];

export function isProbeResponseFormat(
  data: VulnerabilityDataType | undefined
): boolean {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return false;
  }
  const firstItem = data[0];
  return firstItem && typeof firstItem === 'object' && 'responses' in firstItem;
}

export function getProcessedVulnerabilityData(
  data: VulnerabilityDataType | undefined
): any[] {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  if (isProbeResponseFormat(data)) {
    const probeResponses = data as ProbeResponse[];
    const result: any[] = [];

    probeResponses.forEach((probeResponse, probeIndex) => {
      if (!probeResponse?.responses) {
        return;
      }

      // STEP 1: Filter for complete attempts only (status: 2)
      const attemptEntries = probeResponse.responses.filter(
        (entry) => entry && entry.entry_type === 'attempt' && entry.status === 2 // Only ATTEMPT_COMPLETE
      );

      // STEP 2: Deduplicate by UUID
      const deduplicatedAttempts = new Map();

      attemptEntries.forEach((attempt) => {
        if (!attempt?.uuid) {
          return;
        }

        const existing = deduplicatedAttempts.get(attempt.uuid);

        if (!existing) {
          deduplicatedAttempts.set(attempt.uuid, attempt);
        } else {
          // Keep the one with more detector results or higher seq
          const currentDetectorCount = Object.keys(
            attempt.detector_results ?? {}
          ).length;
          const existingDetectorCount = Object.keys(
            existing.detector_results ?? {}
          ).length;

          if (currentDetectorCount > existingDetectorCount) {
            deduplicatedAttempts.set(attempt.uuid, attempt);
          } else if (currentDetectorCount === existingDetectorCount) {
            if ((attempt.seq ?? 0) >= (existing.seq ?? 0)) {
              deduplicatedAttempts.set(attempt.uuid, attempt);
            }
          }
        }
      });

      // STEP 3: Process complete attempts
      Array.from(deduplicatedAttempts.values()).forEach((attempt, index) => {
        if (!attempt) {
          return;
        }

        // STEP 4: Universal detector logic - any score >= threshold = compromised
        let isCompromised = false;

        const detectorResults = attempt.detector_results ?? {};

        // Check all detector results with universal logic
        for (const [detectorName, scores] of Object.entries(detectorResults)) {
          if (Array.isArray(scores) && scores.length > 0) {
            const score = scores[0];

            if (typeof score === 'number') {
              // Universal threshold: any detector score >= 0.5 indicates compromise
              // This works for all detector types:
              // - Encoding: 0.5+ = partial/full decode
              // - DAN: 1.0 = pattern found, 0.0 = no pattern
              // - Others: 0.5+ = vulnerability detected
              if (score >= 0.5) {
                isCompromised = true;
                break; // One positive detection is enough
              }
            }
          }
        }

        result.push({
          category: probeResponse.display_category ?? probeResponse.category,
          artifact_url: probeResponse.artifact_url,
          probeName:
            probeResponse.display_name ??
            probeResponse.probe.split('.').pop() ??
            probeResponse.probe,
          probe_classname: attempt.probe_classname ?? probeResponse.probe,
          goal: `Test resistance to ${probeResponse.display_name ?? probeResponse.category} attacks`,
          prompt: attempt.prompt ?? 'No prompt available',
          outputs: attempt.outputs ?? ['No output available'],
          detector_results: detectorResults,

          // STATUS: 1 = PASS (defended), 2 = FAIL (compromised)
          // Universal logic: score >= 0.5 = compromised
          status: isCompromised ? 2 : 1,

          messages: attempt.messages ?? [
            [
              {
                role: 'user',
                content: attempt.prompt ?? 'No prompt available',
              },
              {
                role: 'assistant',
                content: attempt.outputs?.[0] ?? 'No response available',
              },
            ],
          ],
          uuid: attempt.uuid ?? crypto.randomUUID(),
          seq: attempt.seq ?? index,
        });
      });
    });
    return result;
  }
  return data;
}

export function getVulnerabilityHeaderInfo(
  data: VulnerabilityDataType | undefined
): { category: string; probeName: string } {
  if (!data || data.length === 0) {
    return { category: 'Unknown', probeName: 'Unknown' };
  }
  if (isProbeResponseFormat(data)) {
    const probeResponse = data[0] as ProbeResponse;
    return {
      category: probeResponse.display_category ?? probeResponse.category,
      probeName:
        probeResponse.display_name ??
        probeResponse.probe.split('.').pop() ??
        probeResponse.probe,
    };
  }
  const row = data[0] as { category: string; probeName: string };
  return {
    category: row.category,
    probeName: row.probeName,
  };
}

export function getVulnerabilityGoalInfo(
  data: VulnerabilityDataType | undefined
): string {
  if (!data || data.length === 0) {
    return 'No goal information available';
  }
  if (isProbeResponseFormat(data)) {
    const probeResponse = data[0] as ProbeResponse;
    return `Test resistance to ${probeResponse.display_name ?? probeResponse.category} attacks`;
  }
  const row = data[0] as { goal: string };
  return row.goal || 'No goal information available';
}

export const stringToColor = (string: string) => {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
};

export const stringAvatar = (
  name: string,
  theme?: {
    palette: { background?: { default: string }; text?: { primary: string } };
  }
) => {
  const nameParts = name.trim().split(' ');
  const initials =
    nameParts.length >= 2
      ? `${nameParts[0]?.[0] || ''}${nameParts[1]?.[0] || ''}`
      : nameParts.length === 1
        ? nameParts[0]?.[0] || '?'
        : '?';

  const bgcolor = theme?.palette?.background?.default ?? DesignToken.emptyBg;
  const color = theme?.palette?.text?.primary ?? DesignToken.textPrimary;

  return {
    sx: {
      bgcolor,
      color,
      width: 32,
      height: 32,
      fontSize: '12px',
      marginRight: 1,
    },
    children: initials.toUpperCase(),
  };
};

export const customJurisdictionMapping: JurisdictionMapping = {
  'U.S.A.': ['USA'],
  'United States': ['USA'],
  USA: ['USA'],
  Canada: ['CAN'],
  'United Kingdom': ['GBR'],
  UK: ['GBR'],
  Australia: ['AUS'],
  Japan: ['JPN'],
  China: ['CHN'],
  India: ['IND'],
  Brazil: ['BRA'],
  Germany: ['DEU'],
  France: ['FRA'],
  Italy: ['ITA'],
  Spain: ['ESP'],
  Netherlands: ['NLD'],
  Belgium: ['BEL'],
  Switzerland: ['CHE'],
  Austria: ['AUT'],
  Sweden: ['SWE'],
  Norway: ['NOR'],
  Denmark: ['DNK'],
  Finland: ['FIN'],
  Poland: ['POL'],
  'Czech Republic': ['CZE'],
  Hungary: ['HUN'],
  Slovakia: ['SVK'],
  Slovenia: ['SVN'],
  Croatia: ['HRV'],
  'Bosnia and Herzegovina': ['BIH'],
  Serbia: ['SRB'],
  Montenegro: ['MNE'],
  Macedonia: ['MKD'],
  Albania: ['ALB'],
  Bulgaria: ['BGR'],
  Romania: ['ROU'],
  Greece: ['GRC'],
  Cyprus: ['CYP'],
  Malta: ['MLT'],
  Ireland: ['IRL'],
  Iceland: ['ISL'],
  Estonia: ['EST'],
  Latvia: ['LVA'],
  Lithuania: ['LTU'],
  Luxembourg: ['LUX'],
  Portugal: ['PRT'],
  Russia: ['RUS'],
  Ukraine: ['UKR'],
  Belarus: ['BLR'],
  Moldova: ['MDA'],
  Georgia: ['GEO'],
  Armenia: ['ARM'],
  Azerbaijan: ['AZE'],
  Kazakhstan: ['KAZ'],
  Uzbekistan: ['UZB'],
  Turkmenistan: ['TKM'],
  Kyrgyzstan: ['KGZ'],
  Tajikistan: ['TJK'],
  Afghanistan: ['AFG'],
  Pakistan: ['PAK'],
  Iran: ['IRN'],
  Iraq: ['IRQ'],
  Syria: ['SYR'],
  Lebanon: ['LBN'],
  Jordan: ['JOR'],
  Israel: ['ISR'],
  'West Bank': ['PSE'],
  'Saudi Arabia': ['SAU'],
  Yemen: ['YEM'],
  Oman: ['OMN'],
  'United Arab Emirates': ['ARE'],
  Qatar: ['QAT'],
  Bahrain: ['BHR'],
  Kuwait: ['KWT'],
  Egypt: ['EGY'],
  Libya: ['LBY'],
  Tunisia: ['TUN'],
  Algeria: ['DZA'],
  Morocco: ['MAR'],
  'Western Sahara': ['ESH'],
  Mauritania: ['MRT'],
  Mali: ['MLI'],
  'Burkina Faso': ['BFA'],
  Niger: ['NER'],
  Chad: ['TCD'],
  Sudan: ['SDN'],
  'South Sudan': ['SSD'],
  Ethiopia: ['ETH'],
  Eritrea: ['ERI'],
  Djibouti: ['DJI'],
  Somalia: ['SOM'],
  Somaliland: ['SOM'],
  Kenya: ['KEN'],
  Uganda: ['UGA'],
  Tanzania: ['TZA'],
  Rwanda: ['RWA'],
  Burundi: ['BDI'],
  'Democratic Republic of the Congo': ['COD'],
  'Republic of the Congo': ['COG'],
  'Central African Republic': ['CAF'],
  Cameroon: ['CMR'],
  Nigeria: ['NGA'],
  Gabon: ['GAB'],
  'Equatorial Guinea': ['GNQ'],
  'São Tomé and Príncipe': ['STP'],
  Angola: ['AGO'],
  Zambia: ['ZMB'],
  Zimbabwe: ['ZWE'],
  Botswana: ['BWA'],
  Namibia: ['NAM'],
  'South Africa': ['ZAF'],
  Lesotho: ['LSO'],
  Swaziland: ['SWZ'],
  Mozambique: ['MOZ'],
  Madagascar: ['MDG'],
  Comoros: ['COM'],
  Mauritius: ['MUS'],
  Seychelles: ['SYC'],
  Malawi: ['MWI'],
  Argentina: ['ARG'],
  Bolivia: ['BOL'],
  Chile: ['CHL'],
  Colombia: ['COL'],
  Ecuador: ['ECU'],
  Guyana: ['GUY'],
  Paraguay: ['PRY'],
  Peru: ['PER'],
  Suriname: ['SUR'],
  Uruguay: ['URY'],
  Venezuela: ['VEN'],
  'The Bahamas': ['BHS'],
  Belize: ['BLZ'],
  'Costa Rica': ['CRI'],
  Cuba: ['CUB'],
  'Dominican Republic': ['DOM'],
  'El Salvador': ['SLV'],
  Guatemala: ['GTM'],
  Haiti: ['HTI'],
  Honduras: ['HND'],
  Jamaica: ['JAM'],
  Mexico: ['MEX'],
  Nicaragua: ['NIC'],
  Panama: ['PAN'],
  'Puerto Rico': ['PRI'],
  'Trinidad and Tobago': ['TTO'],
  Bangladesh: ['BGD'],
  Bhutan: ['BTN'],
  Brunei: ['BRN'],
  Cambodia: ['KHM'],
  Indonesia: ['IDN'],
  'East Timor': ['TLS'],
  Laos: ['LAO'],
  Malaysia: ['MYS'],
  Myanmar: ['MMR'],
  Nepal: ['NPL'],
  'New Zealand': ['NZL'],
  'Papua New Guinea': ['PNG'],
  Philippines: ['PHL'],
  'Solomon Islands': ['SLB'],
  'Sri Lanka': ['LKA'],
  Taiwan: ['TWN'],
  Thailand: ['THA'],
  Vietnam: ['VNM'],
  Vanuatu: ['VUT'],
  'New Caledonia': ['NCL'],
  Fiji: ['FJI'],
  'Ivory Coast': ['CIV'],
  Benin: ['BEN'],
  'Guinea Bissau': ['GNB'],
  Guinea: ['GIN'],
  Gambia: ['GMB'],
  Liberia: ['LBR'],
  Senegal: ['SEN'],
  'Sierra Leone': ['SLE'],
  Togo: ['TGO'],
  Ghana: ['GHA'],
  'Falkland Islands': ['FLK'],
  Greenland: ['GRL'],
  Mongolia: ['MXN'],
  'North Korea': ['PRK'],
  'South Korea': ['KOR'],
  Korea: ['KOR'],
  Kosovo: ['XKX'],
  'Northern Cyprus': ['CYP'],
  Turkey: ['TUR'],
  Palestine: ['PSE'],
  // European Union countries (for policy mapping)
  'European Union': [
    'AUT',
    'BEL',
    'BGR',
    'HRV',
    'CYP',
    'CZE',
    'DNK',
    'EST',
    'FIN',
    'FRA',
    'DEU',
    'GRC',
    'HUN',
    'IRL',
    'ITA',
    'LVA',
    'LTU',
    'LUX',
    'MLT',
    'NLD',
    'POL',
    'PRT',
    'ROU',
    'SVK',
    'SVN',
    'ESP',
    'SWE',
  ],
  California: ['USA'],
  Colorado: ['USA'],
  'New York City': ['USA'],
  'New York': ['USA'],
  'South Dakota': ['USA'],
  Iowa: ['USA'],
  Texas: ['USA'],
  Florida: ['USA'],
  Illinois: ['USA'],
  Pennsylvania: ['USA'],
  Ohio: ['USA'],
  Michigan: ['USA'],
};

/** Nivo chart theme keys used by getNivoChartTheme (theme-aware for dark mode). */
interface NivoThemePalette {
  palette: {
    mode?: 'light' | 'dark';
    background: { paper: string };
    text: { primary: string; secondary?: string };
    divider: string;
  };
  shadows: string[];
}

export function getNivoChartTheme(theme: NivoThemePalette) {
  const isDark = theme.palette.mode === 'dark';
  const bg = theme.palette.background.paper;
  const text = theme.palette.text.primary;
  const shadow = theme.shadows[1] ?? 'none';

  // Canvas background for segment separators (Blackstone elegance)
  const canvasBg = isDark ? '#111315' : '#F4F3F0';

  // Softened colors for dark mode - avoid bright white grids
  const gridColor = isDark ? 'rgba(255,255,255,0.06)' : theme.palette.divider;
  const axisColor = isDark ? 'rgba(255,255,255,0.08)' : theme.palette.divider;
  // Muted off-white legends in dark mode
  const labelColor = isDark
    ? (theme.palette.text.secondary ?? '#9CA3AF')
    : text;

  return {
    background: bg,
    textColor: labelColor,
    fontSize: 11,
    axis: {
      domain: {
        line: { stroke: axisColor, strokeWidth: 1 },
      },
      ticks: {
        line: { stroke: axisColor, strokeWidth: 1 },
        text: { fill: labelColor, fontSize: 11 },
      },
      legend: {
        text: { fill: labelColor, fontSize: 12, fontWeight: 500 },
      },
    },
    grid: {
      // Ultra-subtle gridlines in dark mode
      line: { stroke: gridColor, strokeWidth: 1 },
    },
    legends: {
      // Muted legend text for cleaner appearance
      text: { fill: labelColor, fontSize: 11, fontWeight: 400 },
      title: { text: { fill: labelColor, fontSize: 12, fontWeight: 500 } },
    },
    // Pie/donut segment separators - thin dark stroke between segments
    arc: {
      link: { stroke: labelColor, strokeWidth: 1 },
    },
    // For pie/donut charts: use borderWidth and borderColor via component props
    // Chart components should pass: borderWidth={2} borderColor={canvasBg}
    // This creates thin dark separators between segments
    labels: {
      text: { fill: labelColor, fontSize: 11, fontWeight: 500 },
    },
    annotations: {
      text: { fill: labelColor, outlineWidth: 2, outlineColor: bg },
      link: {
        stroke: labelColor,
        strokeWidth: 1,
        outlineWidth: 2,
        outlineColor: bg,
      },
      outline: {
        stroke: labelColor,
        strokeWidth: 2,
        outlineWidth: 2,
        outlineColor: bg,
      },
      symbol: { fill: labelColor, outlineWidth: 2, outlineColor: bg },
    },
    tooltip: {
      container: {
        background: isDark ? '#1F2124' : bg,
        color: isDark ? '#E8EAED' : text,
        fontSize: 12,
        borderRadius: DesignToken.radiusSmall,
        boxShadow: shadow,
        border: isDark ? '1px solid rgba(255,255,255,0.06)' : 'none',
      },
    },
    // Expose canvasBg for chart components to use as borderColor on pie/donut
    _canvasBg: canvasBg,
  };
}

/** @deprecated Use getNivoChartTheme(theme) for theme-aware (dark mode) charts. */
export const customTheme = {
  background: DesignToken.surface1,
  textColor: DesignToken.textPrimary,
  fontSize: 11,
  axis: {
    domain: {
      line: {
        stroke: DesignToken.borderDefault,
        strokeWidth: 1,
      },
    },
    ticks: {
      line: {
        stroke: DesignToken.borderDefault,
        strokeWidth: 1,
      },
      text: {
        fill: DesignToken.textPrimary,
        fontSize: 11,
      },
    },
    legend: {
      text: {
        fill: DesignToken.textPrimary,
        fontSize: 12,
        fontWeight: 'bold',
      },
    },
  },
  grid: {
    line: {
      stroke: DesignToken.borderDefault,
      strokeWidth: 1,
    },
  },
  legends: {
    text: {
      fill: DesignToken.textPrimary,
      fontSize: 11,
    },
  },
  annotations: {
    text: {
      fill: DesignToken.textPrimary,
      outlineWidth: 2,
      outlineColor: DesignToken.surface1,
    },
    link: {
      stroke: DesignToken.textPrimary,
      strokeWidth: 1,
      outlineWidth: 2,
      outlineColor: DesignToken.surface1,
    },
    outline: {
      stroke: DesignToken.textPrimary,
      strokeWidth: 2,
      outlineWidth: 2,
      outlineColor: DesignToken.surface1,
    },
    symbol: {
      fill: DesignToken.textPrimary,
      outlineWidth: 2,
      outlineColor: DesignToken.surface1,
    },
  },
  tooltip: {
    container: {
      background: DesignToken.surface1,
      color: DesignToken.textPrimary,
      fontSize: 12,
      borderRadius: DesignToken.radiusSmall,
      boxShadow: DesignToken.shadowSoft,
    },
  },
};
